import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Workspace, Account, LayoutEntity, LayoutSection } from "../lib/types";
import { usd, pct, shortDate, relDate } from "../lib/format";
import { Icon } from "./icons";
import { HealthRing, HealthBadge, Sparkline, Meter, Avatar, Empty } from "./ui";
import { weeklyUsage, usageSummary, progressFor } from "../lib/usage";
import { buildGraphFromWorkspace } from "../lib/graphFromWorkspace";
import { orgById, contractsForOrg, usersForInstance, personById, bandOf, childOrgs, instancesForOrg, rollupOrg, resolvePerson } from "../lib/graph";
import * as work from "../lib/workClient";

/* The 360 widget catalog. A Layout is an ordered list of these widgets; a
 * detail page renders them via <LayoutGrid>. Each widget declares which
 * entities it supports so the editor only offers the relevant ones. Adding a
 * widget here makes it available in the layout builder — no page changes. */

export interface ProjectData {
  id: string; name: string; accountId: string; health: number;
  region?: string; seatsProvisioned?: number; uptime?: number; version?: string;
}
export interface RelationshipData { orgId: string; name: string; type: string; }
export interface WidgetCtx {
  entity: LayoutEntity;
  ws: Workspace;
  account: Account;
  project?: ProjectData;
  relationship?: RelationshipData;
}

interface WidgetDef {
  label: string;
  entities: LayoutEntity[];
  render: (ctx: WidgetCtx) => React.ReactNode;
}

const bandVar = (h: number) => (h >= 70 ? "var(--good)" : h >= 45 ? "var(--watch)" : "var(--risk)");

function Card({ title, sub, action, children }: { title: string; sub?: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="card__hd"><div><h3>{title}</h3>{sub && <span className="sub">{sub}</span>}</div>{action}</div>
      <div className="card__bd">{children}</div>
    </div>
  );
}

/* ------- usage activity: the weekly bar chart + summary (both entities) ------- */
function UsageActivity({ ctx }: { ctx: WidgetCtx }) {
  const active = ctx.project
    ? (ctx.project.seatsProvisioned && ctx.account.seats ? Math.round(ctx.project.seatsProvisioned * (ctx.account.seatsActive / ctx.account.seats)) : ctx.account.seatsActive)
    : ctx.account.seatsActive;
  const points = weeklyUsage(active, ctx.account.healthTrend);
  const s = usageSummary(points);
  const peak = Math.max(1, ...points.map((p) => p.active));
  return (
    <Card title="Usage activity" sub="Weekly active users · last 12 weeks">
      <div className="row" style={{ gap: 22, flexWrap: "wrap", marginBottom: 14 }}>
        <Kpi label="Active now" value={String(s.activeNow)} foot={`${s.wowPct >= 0 ? "+" : ""}${s.wowPct}% WoW`} color={s.wowPct >= 0 ? "var(--good)" : "var(--risk)"} />
        <Kpi label="Events (12w)" value={s.totalEvents.toLocaleString()} foot="feature interactions" />
        <Kpi label="Per active user" value={String(s.avgPerUser)} foot="events / user / wk" />
      </div>
      <div className="usage-bars">
        {points.map((p) => (
          <div key={p.label} className="usage-bar" title={`${p.label}: ${p.active} active · ${p.events} events`}>
            <span style={{ height: `${Math.max(4, (p.active / peak) * 100)}%` }} />
            <small>{p.label}</small>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ------- progress: onboarding / adoption / value + milestones ------- */
function ProgressWidget({ ctx }: { ctx: WidgetCtx }) {
  const a = ctx.account;
  const p = progressFor({ implementationScore: Number(a.attributes?.implementation_score) || undefined, seats: a.seats, activeSeats: a.seatsActive, health: a.health, goLive: String(a.attributes?.go_live_date || "") || undefined });
  return (
    <Card title="Progress" sub="Onboarding, adoption, and value realized">
      <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
        <ProgressRow label="Onboarding" value={p.onboarding} />
        <ProgressRow label="Adoption" value={p.adoption} />
        <ProgressRow label="Value realized" value={p.valueRealized} />
      </div>
      <div className="labeled__l" style={{ marginBottom: 8 }}>Milestones</div>
      <div style={{ display: "grid", gap: 7 }}>
        {p.milestones.map((m) => (
          <div key={m.label} className="row" style={{ gap: 9, fontSize: 13 }}>
            <span style={{ color: m.done ? "var(--good)" : "var(--faint)", display: "inline-flex" }}><Icon name={m.done ? "check" : "dot"} size={15} /></span>
            <span style={{ color: m.done ? "var(--ink)" : "var(--faint)" }}>{m.label}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}
function ProgressRow({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="row between" style={{ marginBottom: 5 }}><span style={{ fontSize: 13 }}>{label}</span><b style={{ fontSize: 13, color: bandVar(value) }}>{value}%</b></div>
      <Meter value={value} color={bandVar(value)} />
    </div>
  );
}

function AiSummaryWidget({ ctx }: { ctx: WidgetCtx }) {
  const [summary, setSummary] = useState<work.AiSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [live, setLive] = useState<boolean | null>(null);
  useEffect(() => { work.aiStatus().then((r) => setLive(r.available)); }, []);
  const gen = async () => { setLoading(true); setSummary(await work.accountAiSummary(ctx.ws, ctx.account.id)); setLoading(false); };
  return (
    <Card title="AI summary" sub="Health narrative & next move"
      action={live !== null ? <span className={`mode-pill ${live ? "live" : ""}`}><i />{live ? "Live AI" : "Built-in"}</span> : undefined}>
      {summary ? (
        <>
          <p style={{ fontSize: 14, lineHeight: 1.6 }}>{summary.text}</p>
          <button className="btn btn--ghost btn--sm" style={{ marginTop: 10 }} onClick={gen} disabled={loading}>{loading ? "…" : "Regenerate"}</button>
        </>
      ) : (
        <div className="row between" style={{ alignItems: "center" }}>
          <span className="faint" style={{ fontSize: 13 }}>Generate a briefing on {ctx.account.name}.</span>
          <button className="btn btn--primary btn--sm" onClick={gen} disabled={loading}><Icon name="sparkle" size={14} /> {loading ? "Thinking…" : "Generate"}</button>
        </div>
      )}
    </Card>
  );
}

function Kpi({ label, value, foot, color }: { label: string; value: string; foot?: string; color?: string }) {
  return (
    <div>
      <div className="labeled__l">{label}</div>
      <div style={{ fontFamily: "var(--serif)", fontSize: 24, lineHeight: 1, marginTop: 4, color }}>{value}</div>
      {foot && <div className="faint" style={{ fontSize: 12, marginTop: 3 }}>{foot}</div>}
    </div>
  );
}

/* ------- churn & expansion prediction: scores + ranked drivers ------- */
function riskColor(v: number) { return v >= 60 ? "var(--risk)" : v >= 35 ? "var(--watch)" : "var(--good)"; }
function DriverList({ drivers, up }: { drivers: work.Driver[]; up: boolean }) {
  if (!drivers.length) return <div className="faint" style={{ fontSize: 12 }}>No material drivers.</div>;
  const max = Math.max(...drivers.map((d) => Math.abs(d.impact)), 1);
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {drivers.slice(0, 5).map((d) => (
        <div key={d.label}>
          <div className="row between" style={{ marginBottom: 3 }}>
            <span style={{ fontSize: 13 }}>{d.label}</span>
            <b style={{ fontSize: 12, color: up ? "var(--good)" : "var(--risk)" }}>{d.impact > 0 ? "+" : ""}{d.impact}</b>
          </div>
          <div style={{ height: 4, background: "var(--line)", borderRadius: 3, overflow: "hidden" }}>
            <span style={{ display: "block", height: "100%", width: `${(Math.abs(d.impact) / max) * 100}%`, background: up ? "var(--good)" : "var(--risk)" }} />
          </div>
          <div className="faint" style={{ fontSize: 11, marginTop: 2 }}>{d.detail}</div>
        </div>
      ))}
    </div>
  );
}
function PredictionWidget({ ctx }: { ctx: WidgetCtx }) {
  const [p, setP] = useState<work.Prediction | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  useEffect(() => { let live = true; work.loadPrediction(ctx.ws, ctx.account.id).then((r) => { if (live) { setP(r); setLoading(false); } }); return () => { live = false; }; }, [ctx.ws, ctx.account.id]);
  if (loading) return <Card title="Churn & expansion" sub="Explainable prediction"><div className="faint" style={{ fontSize: 13 }}>Scoring…</div></Card>;
  if (!p) return <Card title="Churn & expansion" sub="Explainable prediction"><Empty>No prediction available.</Empty></Card>;
  return (
    <Card title="Churn & expansion" sub="Explainable prediction"
      action={<span className={`badge badge--${p.band === "high" ? "risk" : p.band === "medium" ? "watch" : "good"}`}>{p.band} risk</span>}>
      <div className="row" style={{ gap: 22, flexWrap: "wrap", marginBottom: 16 }}>
        <Kpi label="Churn risk" value={`${p.churnRisk}%`} foot="probability of churn" color={riskColor(p.churnRisk)} />
        <Kpi label="Expansion" value={`${p.expansion}%`} foot="propensity to grow" color={p.expansion >= 50 ? "var(--good)" : "var(--faint)"} />
        <Kpi label="ARR at stake" value={usd(p.arr)} foot="annual recurring" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div>
          <div className="labeled__l" style={{ marginBottom: 8, color: "var(--risk)" }}>Why churn risk</div>
          <DriverList drivers={p.churnDrivers} up={false} />
        </div>
        <div>
          <div className="labeled__l" style={{ marginBottom: 8, color: "var(--good)" }}>Why expansion</div>
          <DriverList drivers={p.expansionDrivers} up={true} />
        </div>
      </div>
    </Card>
  );
}

/* ------- relationship widgets (entity: "relationship") ------- */
function useRelGraph(ctx: WidgetCtx) {
  const g = useMemo(() => buildGraphFromWorkspace(ctx.ws), [ctx.ws]);
  const orgId = ctx.relationship?.orgId ?? ctx.account.id;
  const org = orgById(g, orgId);
  const members = childOrgs(g, orgId).length ? childOrgs(g, orgId) : (org ? [org] : []);
  const memberAccounts = members.map((m) => ctx.ws.accounts.find((a) => a.id === (m.accountId ?? m.id))).filter((a): a is Account => !!a);
  return { g, orgId, org, members, memberAccounts };
}
function RelOverviewWidget({ ctx }: { ctx: WidgetCtx }) {
  const { g, orgId, memberAccounts } = useRelGraph(ctx);
  const roll = rollupOrg(g, orgId);
  const arr = memberAccounts.reduce((s, a) => s + a.arr, 0);
  return (
    <Card title="Relationship at a glance" sub={`${ctx.relationship?.name ?? ""} · rolls up ${memberAccounts.length} account${memberAccounts.length === 1 ? "" : "s"}`}>
      <div className="row" style={{ gap: 22, flexWrap: "wrap" }}>
        <Kpi label="Total ARR" value={usd(arr || roll.revenue)} foot={`${roll.contracts} contracts`} />
        <Kpi label="Avg health" value={String(roll.health)} foot="across deployments" color={bandVar(roll.health)} />
        <Kpi label="Accounts" value={String(memberAccounts.length)} foot="in this relationship" />
        <Kpi label="Deployments" value={String(roll.instances)} foot="running" />
        <Kpi label="Active users" value={String(roll.activeUsers)} foot={`of ${roll.people} people`} />
      </div>
    </Card>
  );
}
function RelAccountsWidget({ ctx }: { ctx: WidgetCtx }) {
  const { memberAccounts } = useRelGraph(ctx);
  return (
    <Card title="Accounts" sub="Every account in this relationship">
      {memberAccounts.length === 0 ? <Empty>No accounts.</Empty> : (
        <div style={{ display: "grid", gap: 8 }}>
          {memberAccounts.map((a) => (
            <Link key={a.id} to={`/accounts/${a.id}`} className="affil" style={{ textDecoration: "none" }}>
              <span className="t-ic" style={{ width: 28, height: 28, background: "var(--surface-2)", color: "var(--ink)" }}>{a.name[0]}</span>
              <div className="grow"><b style={{ fontSize: 13 }}>{a.name}</b><div className="faint" style={{ fontSize: 12 }}>{usd(a.arr)} · {a.region}</div></div>
              <HealthBadge band={bandOf(a.health)} label={`${a.health}`} />
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}
function RelStructureWidget({ ctx }: { ctx: WidgetCtx }) {
  const { g, org } = useRelGraph(ctx);
  const parent = org?.parentOrgId ? orgById(g, org.parentOrgId) : undefined;
  const children = org ? childOrgs(g, org.id) : [];
  return (
    <Card title="Corporate structure" sub="How this relationship is organized">
      {org && (
        <div style={{ display: "grid", gap: 8 }}>
          {parent && <div className="affil"><span className="t-ic" style={{ width: 28, height: 28, background: parent.color }}>{parent.name[0]}</span><div className="grow"><b style={{ fontSize: 13 }}>{parent.name}</b><div className="faint" style={{ fontSize: 12 }}>Parent</div></div></div>}
          <div className="affil" style={{ marginLeft: parent ? 16 : 0, borderLeft: parent ? "2px solid var(--line)" : "none", paddingLeft: parent ? 10 : 0 }}>
            <span className="t-ic" style={{ width: 28, height: 28, background: org.color }}>{org.name[0]}</span>
            <div className="grow"><b style={{ fontSize: 13 }}>{org.name}</b><div className="faint" style={{ fontSize: 12 }}>{org.type}</div></div>
            <span className="tag">this relationship</span>
          </div>
          {children.map((c) => (
            <div key={c.id} className="affil" style={{ marginLeft: 16, borderLeft: "2px solid var(--line)", paddingLeft: 10 }}>
              <span className="t-ic" style={{ width: 26, height: 26, background: c.color }}>{c.name[0]}</span>
              <div className="grow">{c.accountId ? <Link to={`/accounts/${c.accountId}`} style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</Link> : <b style={{ fontSize: 13 }}>{c.name}</b>}<div className="faint" style={{ fontSize: 12 }}>{c.type}</div></div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
function RelContractsWidget({ ctx }: { ctx: WidgetCtx }) {
  const { g, members } = useRelGraph(ctx);
  const contracts = members.flatMap((m) => contractsForOrg(g, m.id));
  return (
    <Card title="Contracts" sub={`${contracts.length} across the relationship`}>
      {contracts.length === 0 ? <Empty>No contracts.</Empty> : (
        <div style={{ overflowX: "auto" }}>
          <table className="table">
            <thead><tr><th>Contract</th><th>Type</th><th style={{ textAlign: "right" }}>Value</th><th>Term</th><th>Status</th></tr></thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={c.id} style={{ cursor: "default" }}>
                  <td><b style={{ fontSize: 13 }}>{c.name}</b></td>
                  <td className="faint" style={{ fontSize: 12.5 }}>{c.revenueType}</td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{usd(c.amount)}</td>
                  <td className="faint" style={{ fontSize: 12.5 }}>{shortDate(c.startDate)} → {shortDate(c.endDate)}</td>
                  <td><span className="tag">{c.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
function RelContactsWidget({ ctx }: { ctx: WidgetCtx }) {
  const { g, members } = useRelGraph(ctx);
  const instanceIds = new Set(members.flatMap((m) => instancesForOrg(g, m.id)).map((i) => i.id));
  const personIds = [...new Set(g.users.filter((u) => instanceIds.has(u.instanceId)).map((u) => u.personId))];
  const people = personIds.map((pid) => resolvePerson(g, pid)).filter((r): r is NonNullable<typeof r> => r !== null);
  return (
    <Card title="People across the relationship" sub="Contacts resolved over every account & login">
      {people.length === 0 ? <Empty>No people resolved.</Empty> : (
        <div style={{ display: "grid", gap: 8 }}>
          {people.map((res) => {
            const orgs = res.appearsUnder.filter((a) => a.org.accountId);
            return (
              <div className="affil" key={res.person.id}>
                <Avatar name={res.person.name} size="sm" />
                <div className="grow"><b style={{ fontSize: 13 }}>{res.person.name}</b><span className="faint" style={{ fontSize: 12 }}> · {res.person.title}</span>
                  {orgs.length > 1 && <div className="faint" style={{ fontSize: 12 }}>Spans {orgs.map((o) => o.org.name).join(", ")}</div>}</div>
                <span className="tag">{res.users.length} login{res.users.length === 1 ? "" : "s"}</span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export const WIDGET_DEFS: Record<string, WidgetDef> = {
  aiSummary: { label: "AI summary", entities: ["account", "project"], render: (ctx) => <AiSummaryWidget ctx={ctx} /> },
  prediction: { label: "Churn & expansion prediction", entities: ["account"], render: (ctx) => <PredictionWidget ctx={ctx} /> },
  relOverview: { label: "Relationship overview", entities: ["relationship"], render: (ctx) => <RelOverviewWidget ctx={ctx} /> },
  relAccounts: { label: "Accounts in relationship", entities: ["relationship"], render: (ctx) => <RelAccountsWidget ctx={ctx} /> },
  relStructure: { label: "Corporate structure", entities: ["relationship"], render: (ctx) => <RelStructureWidget ctx={ctx} /> },
  relContracts: { label: "Contracts", entities: ["relationship"], render: (ctx) => <RelContractsWidget ctx={ctx} /> },
  relContacts: { label: "People", entities: ["relationship"], render: (ctx) => <RelContactsWidget ctx={ctx} /> },

  health: {
    label: "Health", entities: ["account", "project"],
    render: (ctx) => {
      if (ctx.entity === "project" && ctx.project) {
        return (
          <Card title="Health" sub="This deployment">
            <div className="row" style={{ gap: 16, alignItems: "center" }}>
              <HealthRing value={ctx.project.health} size={64} stroke={6} />
              <div><HealthBadge band={bandOf(ctx.project.health)} label={`${ctx.project.health}`} /><div className="faint" style={{ fontSize: 12, marginTop: 6 }}>vs {ctx.account.health} account avg</div></div>
              <div style={{ marginLeft: "auto" }}><Sparkline data={ctx.account.healthTrend} band={ctx.account.healthBand} width={140} height={40} /></div>
            </div>
          </Card>
        );
      }
      return (
        <Card title="Health composition" sub={`Weighted factors behind ${ctx.account.health}`}>
          {ctx.account.scoreFactors.map((f) => (
            <div key={f.label} style={{ padding: "9px 0", borderBottom: "1px solid var(--line-soft)" }}>
              <div className="row between" style={{ marginBottom: 6 }}>
                <span className="row" style={{ gap: 8, fontSize: 13 }}><Icon name={f.direction === "up" ? "arrowUp" : f.direction === "down" ? "arrowDown" : "dot"} size={14} /> {f.label} <span className="tag">{pct(f.weight * 100)}</span></span>
                <b style={{ color: bandVar(f.value), fontSize: 13 }}>{f.value}</b>
              </div>
              <Meter value={f.value} color={bandVar(f.value)} />
            </div>
          ))}
        </Card>
      );
    },
  },

  adoption: {
    label: "Adoption", entities: ["account", "project"],
    render: (ctx) => {
      const seats = ctx.project?.seatsProvisioned ?? ctx.account.seats;
      const active = ctx.project
        ? Math.round((ctx.project.seatsProvisioned ?? ctx.account.seats) * (ctx.account.seatsActive / (ctx.account.seats || 1)))
        : ctx.account.seatsActive;
      const util = seats ? Math.round((active / seats) * 100) : 0;
      return (
        <Card title="Adoption" sub="Active seats & 12-week trend">
          <div className="row between" style={{ marginBottom: 8 }}>
            <div><div style={{ fontFamily: "var(--serif)", fontSize: 26 }}>{util}%</div><div className="faint" style={{ fontSize: 12 }}>{active} of {seats} seats active</div></div>
            <Sparkline data={ctx.account.healthTrend} band={ctx.account.healthBand} width={150} height={40} />
          </div>
          <Meter value={util} color={bandVar(util)} />
        </Card>
      );
    },
  },

  usageActivity: { label: "Usage activity", entities: ["account", "project"], render: (ctx) => <UsageActivity ctx={ctx} /> },

  progress: { label: "Progress & milestones", entities: ["account", "project"], render: (ctx) => <ProgressWidget ctx={ctx} /> },

  signals: {
    label: "Signals", entities: ["account"],
    render: (ctx) => {
      const sigs = ctx.ws.signals.filter((s) => s.accountId === ctx.account.id);
      return (
        <Card title="Signals" sub={`${sigs.length} detected on this account`}>
          {sigs.length === 0 ? <Empty>No signals.</Empty> : <div style={{ display: "grid", gap: 8 }}>
            {sigs.map((s) => (
              <div key={s.id} className="row" style={{ gap: 9, alignItems: "flex-start" }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", marginTop: 5, background: s.band === "positive" ? "var(--good)" : s.band === "watch" ? "var(--watch)" : "var(--risk)", flex: "none" }} />
                <div><b style={{ fontSize: 13 }}>{s.title}</b><div className="faint" style={{ fontSize: 12 }}>{s.detail} · {relDate(s.at)}</div></div>
              </div>
            ))}
          </div>}
        </Card>
      );
    },
  },

  successPlan: {
    label: "Success plan", entities: ["account"],
    render: (ctx) => {
      const plan = ctx.ws.successPlans.find((p) => p.accountId === ctx.account.id);
      if (!plan) return <Card title="Success plan"><Empty>No success plan yet.</Empty></Card>;
      return (
        <Card title={`Success plan · ${plan.title}`} sub={plan.outcome}>
          <div style={{ display: "grid", gap: 7 }}>
            {plan.objectives.map((o) => (
              <div key={o.id} className="row between" style={{ fontSize: 13 }}>
                <span>{o.title}</span>
                <span className="badge" style={{ color: o.status === "done" ? "var(--good)" : o.status === "blocked" ? "var(--risk)" : o.status === "in_progress" ? "var(--watch)" : "var(--faint)", borderColor: "currentColor" }}>{o.status.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </Card>
      );
    },
  },

  renewal: {
    label: "Renewal", entities: ["account"],
    render: (ctx) => {
      const rn = ctx.ws.renewals.find((r) => r.accountId === ctx.account.id);
      return (
        <Card title="Renewal" sub="Commercial outlook">
          <div className="kv">
            <dt>Renews</dt><dd>{shortDate(rn?.renewalDate ?? ctx.account.renewalDate)}</dd>
            <dt>ARR</dt><dd>{usd(rn?.arr ?? ctx.account.arr)}</dd>
            {rn && <><dt>Forecast</dt><dd style={{ textTransform: "capitalize" }}>{rn.category.replace("_", " ")}</dd></>}
            {rn && rn.expansionAmount > 0 && <><dt>Expansion</dt><dd>+{usd(rn.expansionAmount)}</dd></>}
          </div>
        </Card>
      );
    },
  },

  users: {
    label: "Users on this deployment", entities: ["project"],
    render: (ctx) => {
      if (!ctx.project) return null;
      const g = buildGraphFromWorkspace(ctx.ws);
      const users = usersForInstance(g, ctx.project.id);
      return (
        <Card title="Users" sub={`${users.length} on this deployment`}>
          {users.length === 0 ? <Empty>No users.</Empty> : <div style={{ display: "grid", gap: 8 }}>
            {users.map((u) => {
              const p = personById(g, u.personId);
              return (
                <div key={u.id} className="row" style={{ gap: 9 }}>
                  <Avatar name={p?.name ?? "?"} size="sm" />
                  <div className="grow"><b style={{ fontSize: 13 }}>{p?.name}</b><span className="faint" style={{ fontSize: 12 }}> · {u.role}</span></div>
                  <span className={`badge ${u.active ? "badge--good" : "badge--neutral"}`}>{u.active ? `active ${relDate(u.lastActive)}` : "inactive"}</span>
                </div>
              );
            })}
          </div>}
        </Card>
      );
    },
  },

  deploymentInfo: {
    label: "Deployment details", entities: ["project"],
    render: (ctx) => {
      if (!ctx.project) return null;
      return (
        <Card title="Deployment details" sub="Infrastructure & version">
          <div className="kv">
            <dt>Region</dt><dd>{ctx.project.region ?? "—"}</dd>
            <dt>Seats provisioned</dt><dd>{ctx.project.seatsProvisioned ?? "—"}</dd>
            <dt>Uptime (30d)</dt><dd>{ctx.project.uptime != null ? `${ctx.project.uptime}%` : "—"}</dd>
            <dt>Version</dt><dd>{ctx.project.version ?? "—"}</dd>
          </div>
        </Card>
      );
    },
  },

  hierarchy: {
    label: "Where this sits", entities: ["project"],
    render: (ctx) => {
      const g = buildGraphFromWorkspace(ctx.ws);
      const org = orgById(g, ctx.account.id);
      const parent = org?.parentOrgId ? orgById(g, org.parentOrgId) : undefined;
      const contract = contractsForOrg(g, ctx.account.id)[0];
      return (
        <Card title="Where this sits" sub="Account · contract · deployment">
          <div style={{ display: "grid", gap: 6, fontSize: 13 }}>
            {parent && <div className="row" style={{ gap: 8 }}><Icon name="building" size={14} className="faint" /> {parent.name} <span className="faint">(parent)</span></div>}
            <div className="row" style={{ gap: 8 }}><Icon name="building" size={14} className="faint" /> <Link to={`/accounts/${ctx.account.id}`}>{ctx.account.name}</Link></div>
            {contract && <div className="row" style={{ gap: 8 }}><Icon name="target" size={13} className="faint" /> {contract.name} · {usd(contract.amount)}</div>}
            <div className="row" style={{ gap: 8 }}><Icon name="layers" size={13} className="faint" /> <b>{ctx.project?.name}</b></div>
          </div>
          <Link to={`/relationships?account=${ctx.account.id}`} className="btn btn--ghost btn--sm" style={{ marginTop: 12 }}>Open full graph <Icon name="arrowRight" size={13} /></Link>
        </Card>
      );
    },
  },

  contacts: {
    label: "Contacts", entities: ["account"],
    render: (ctx) => (
      <Card title="Contacts" sub={`${ctx.account.contacts.length} on this account`}>
        <div style={{ display: "grid", gap: 8 }}>
          {ctx.account.contacts.map((c) => (
            <div key={c.email} className="row" style={{ gap: 9 }}>
              <Avatar name={c.name} size="sm" />
              <div className="grow"><b style={{ fontSize: 13 }}>{c.name}</b><span className="faint" style={{ fontSize: 12 }}> · {c.title}</span></div>
              <span className="tag">{c.role}</span>
            </div>
          ))}
        </div>
      </Card>
    ),
  },
};

/** Render a layout's enabled sections into a responsive grid. */
export function LayoutGrid({ sections, ctx }: { sections: LayoutSection[]; ctx: WidgetCtx }) {
  const visible = sections.filter((s) => s.enabled && WIDGET_DEFS[s.widget]?.entities.includes(ctx.entity));
  if (visible.length === 0) return <Empty>No widgets on this layout yet — click Customize to add some.</Empty>;
  return (
    <div className="layout-grid">
      {visible.map((s) => (
        <div key={s.id} className={`layout-cell ${s.span === "half" ? "layout-cell--half" : "layout-cell--full"}`}>
          {WIDGET_DEFS[s.widget].render(ctx)}
        </div>
      ))}
    </div>
  );
}
